import { inject } from "fw";
import { Store, handle } from "fw-state";

import { LogoutAction, StartAction } from "./actions";
import { UserType } from "models/user";
import { AtsOrganizationMappingDetail } from "models/ats-organization-summary";
import { AtsOrgMappingDetailRepository } from "network/ats-org-mapping-detail-repository";
import { ReloadOrganizationOnEnsureAction, UpdatedOrganizationAction } from "../forms/organization";
import { EntitySelection } from "models/ats-organization";

interface CurrentAtsOrganizationSummaryShape {
    currentUserType: UserType;
    atsOrganizationId: string;
    loaded: boolean;
    errorLoading: boolean;
    summary: AtsOrganizationMappingDetail;
    canRemove: boolean;
    canAdd: boolean;
}

export class CurrentAtsOrgSummaryEnsureAction {
    constructor(public atsOrganizationId: string = null) {}
}

export class CurrentAtsOrgSummaryRefreshAction {}
export class CurrentAtsOrgSummaryClearAction {}

export class CurrentAtsApplyTagAction {
    constructor(
        public selection: EntitySelection,
        public addTags: string[],
        public removeTags: string[]
    ) {}
}

@inject
export class CurrentAtsOrganizationSummaryStore extends Store<CurrentAtsOrganizationSummaryShape> {
    constructor(private atsOrgMappingDetailRepository: AtsOrgMappingDetailRepository) {
        super();
    }

    defaultState() {
        return {
            currentUserType: null,
            atsOrganizationId: null,
            loaded: false,
            errorLoading: false,
            summary: null,
            canRemove: false,
            canAdd: false
        };
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserType: action.user.Type
        }));
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.setState(() => this.defaultState());
    }

    @handle(CurrentAtsOrgSummaryClearAction)
    private handleClearAction(action: CurrentAtsOrgSummaryClearAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserType: this.state.currentUserType,
            loaded: true
        }));
    }

    @handle(CurrentAtsOrgSummaryEnsureAction)
    private async handleEnsureAction(action: CurrentAtsOrgSummaryEnsureAction) {
        const { atsOrganizationId, currentUserType, loaded } = this.state;

        if (action.atsOrganizationId === atsOrganizationId && loaded) return;

        this.setState(() => ({
            ...this.defaultState(),
            currentUserType,
            atsOrganizationId: action.atsOrganizationId
        }));

        await this.load(action.atsOrganizationId);
    }

    @handle(CurrentAtsOrgSummaryRefreshAction)
    private async handleRefresh(action: CurrentAtsOrgSummaryRefreshAction) {
        const { atsOrganizationId, loaded } = this.state;

        if (!loaded) return;

        this.setState((state) => ({
            ...state,
            errorLoading: false
        }));

        await this.load(atsOrganizationId);
    }

    private async load(atsOrganizationId: string) {
        const { currentUserType } = this.state;
        if (this.state.loaded) {
            this.setState((state) => ({
                ...state,
                loaded: false
            }));
        }

        if (!atsOrganizationId) {
            return;
        }

        try {
            const summary = await this.atsOrgMappingDetailRepository.getByOrganizationId(atsOrganizationId);
            this.setState((state) => ({
                ...state,
                loaded: true,
                errorLoading: false,
                summary,
                canRemove: this.canRemove(currentUserType, summary),
                canAdd: this.canAdd(currentUserType)
            }));
        } catch {
            this.setState((state) => ({
                ...state,
                loaded: true,
                errorLoading: true
            }));
            console.error("Something went wrong with finding the organization.");
        }
    }

    private canRemove(type: UserType, summary: AtsOrganizationMappingDetail) {
        if (!summary?.Mapping || type !== UserType.SystemAdministrator) return false;

        return !summary.Mapping.MetaData.CurrentSyncTaskId;
    }

    private canAdd(type: UserType) {
        return type === UserType.SystemAdministrator;
    }

    @handle(UpdatedOrganizationAction)
    private async handleUpdatedOrganizationAction(action: UpdatedOrganizationAction) {
        if (action.organization.Id === this.state.atsOrganizationId) {
            await this.load(action.organization.Id);
        }
    }

    @handle(ReloadOrganizationOnEnsureAction)
    private async handleReloadOrganizationOnEnsureAction(action: ReloadOrganizationOnEnsureAction) {
        if (action.organizationId === this.state.atsOrganizationId) {
            this.setState((state) => ({
                ...state,
                loaded: false
            }));
        }
    }

    @handle(CurrentAtsApplyTagAction)
    private async handleCurrentAtsApplyTagAction(action: CurrentAtsApplyTagAction) {
        if (!this.state.summary.Organization || !action.selection.ids.includes(this.state.summary.Organization.Id)) return;

        let tags = this.state.summary.Organization.Tags ?? [];

        action.addTags.forEach((tag) => !tags.includes(tag) && tags.push(tag));
        action.removeTags.forEach((tag) => {
            if (tags.includes(tag)) tags = tags.filter((item) => item !== tag);
        });

        this.setState((s) => ({
            ...s,
            summary: {
                ...s.summary,
                Organization: {
                    ...s.summary.Organization,
                    Tags: tags
                }
            }
        }));
    }
}
